import React, {
  useEffect, useState, useRef, useCallback, useMemo,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, User, CheckCircle, Plus, Copy, Download, RefreshCw, Activity, PenTool, Archive, ArrowLeft } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { getTaskTitle, getTaskFormat, getTaskStyle } from '../utils/statsUtils';
import Logo from '../components/Logo';
import { timeAgo, formatTime } from '../utils/timeUtils';
import { compactToolDetail, formatToolActivityText } from '../utils/toolActivity';

// ─── Agent colors ─────────────────────────────────────────────────────────────
const AGENT_COLORS = {
  'Content Strategist':                     '#6366F1',
  'Research Writer':                        '#0EA5E9',
  'Fact-Checker / Verification Specialist': '#10B981',
  'Fact-Checker':                           '#10B981',
  'Managing Editor':                        '#F59E0B',
  'SEO Specialist & Copywriter':            '#EC4899',
  'SEO Specialist':                         '#EC4899',
  'Philosopher / Thought Leader':           '#8B5CF6',
  'Subject Matter Expert (SME)':            '#14B8A6',
  'Subject Matter Expert':                  '#14B8A6',
  'Quality Control':                        '#6366F1',
  'System':                                 '#6B7280',
  'You':                                    '#374151',
};

function getAgentColor(name) {
  if (!name) return '#6B7280';
  if (AGENT_COLORS[name]) return AGENT_COLORS[name];
  const entry = Object.entries(AGENT_COLORS).find(([k]) =>
    name.toLowerCase().includes(k.toLowerCase().split(' ')[0])
  );
  return entry ? entry[1] : '#1E3A8A';
}

// ─── Agent metadata ───────────────────────────────────────────────────────────
const AGENT_MAP = {
  'Content Strategist':                     { abbr: 'CS' },
  'Research Writer':                        { abbr: 'RW' },
  'Managing Editor':                        { abbr: 'ME' },
  'SEO Specialist & Copywriter':            { abbr: 'SE' },
  'SEO Specialist':                         { abbr: 'SE' },
  'Fact-Checker / Verification Specialist': { abbr: 'FC' },
  'Fact-Checker':                           { abbr: 'FC' },
  'Philosopher / Thought Leader':           { abbr: 'TL' },
  'Subject Matter Expert (SME)':            { abbr: 'SM' },
  'Subject Matter Expert':                  { abbr: 'SM' },
  'Quality Control':                        { abbr: 'QC' },
  'System':                                 { abbr: 'SY' },
  'You':                                    { abbr: 'ME' },
};

const STEP_AGENTS = {
  1: 'Content Strategist', 2: 'Research Writer', 3: 'Research Writer',
  4: 'Research Writer',    5: 'Fact-Checker / Verification Specialist',
  6: 'Managing Editor',    7: 'Content Strategist',
  8: 'SEO Specialist & Copywriter', 9: 'Content Strategist',
};

const STEP_NAMES = {
  1: 'Topic Selection & Strategy Planning',
  2: 'Research & Information Gathering',
  3: 'Outline & Structure Development',
  4: 'First Draft Creation',
  5: 'Fact-Checking & Verification',
  6: 'Multi-Layered Editing',
  7: 'Final Approval & Strategy Check',
  8: 'Publication & Distribution',
  9: 'Post-Publication Monitoring',
};

const FORMAT_STEP_COUNT = {
  'Books': 9, 'White Papers': 9, 'Case Studies': 9,
  'Articles': 9, 'Blog Posts': 9, 'eBooks': 9, 'Research Reports': 9,
  'Marketing & Sales Copy': 7, 'Technical Manuals & Documentation': 7,
  'Speeches & Presentations': 4, 'Press Releases': 7,
  'Web Content & Landing Pages': 8, 'Proposals & Grants': 7,
  'Thought Leadership Essays': 7,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getAgentAbbr(name) {
  if (!name) return 'AG';
  if (AGENT_MAP[name]) return AGENT_MAP[name].abbr;
  const entry = Object.entries(AGENT_MAP).find(([k]) =>
    name.toLowerCase().includes(k.toLowerCase().split(' ')[0])
  );
  if (entry) return entry[1].abbr;
  return name.replace(/_and_/gi, ' & ')
    .split(/[_ ]/).filter(Boolean)
    .map(w => w[0]).join('').substring(0, 2).toUpperCase();
}

function formatAgentName(raw) {
  if (!raw) return 'Agent';
  return raw
    .replace(/_and_/gi, ' & ').replace(/__/g, ' / ').replace(/_/g, ' ')
    .replace(/\bSme\b/gi, 'SME').replace(/\bSeo\b/gi, 'SEO').trim();
}

function getTotalTokens(instance) {
  const contextTotal = instance?.context_memory?.total_tokens_used;
  if (typeof contextTotal === 'number' && contextTotal > 0) return contextTotal;

  const tokenUsage = instance?.token_usage;
  if (!tokenUsage || typeof tokenUsage !== 'object') return 0;

  let total = 0;
  Object.values(tokenUsage).forEach((value) => {
    if (typeof value === 'number') {
      total += value;
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value).forEach((nestedValue) => {
        if (typeof nestedValue === 'number') total += nestedValue;
      });
    }
  });

  return total;
}

// ─── getBestContent ───────────────────────────────────────────────────────────
function getBestContent(instance) {
  if (!instance) return null;
  const cm = instance.context_memory || {};

  const priorityKeys = [
    'final_content',
    'step_7_final', 'step_6_edited', 'step_4_draft',
    'step_5_factcheck', 'step_3_outline', 'step_2_research', 'step_1_strategy',
    'step_7_final_approval__strategy_check',
    'step_7_final_approval_strategy_check',
    'step_6_multi_layered_editing',
    'step_4_first_draft_creation',
    'step_5_fact_checking__verification',
    'step_5_fact_checking_verification',
    'step_3_outline__structure_development',
    'step_3_outline_structure_development',
    'step_2_research__information_gathering',
    'step_2_research_information_gathering',
    'step_1_topic_selection__strategy_planning',
    'step_1_topic_selection_strategy_planning',
  ];

  for (const key of priorityKeys) {
    const value = cm[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }

  return null;
}

const PERSISTED_STEP_CONTENT_KEYS = {
  1: ['step_1_strategy', 'step_1_topic_selection__strategy_planning', 'step_1_topic_selection_strategy_planning'],
  2: ['step_2_research', 'step_2_research__information_gathering', 'step_2_research_information_gathering'],
  3: ['step_3_outline', 'step_3_outline__structure_development', 'step_3_outline_structure_development'],
  4: ['step_4_draft', 'step_4_first_draft_creation'],
  5: ['step_5_factcheck', 'step_5_fact_checking__verification', 'step_5_fact_checking_verification'],
  6: ['step_6_edited', 'step_6_multi_layered_editing'],
  7: ['step_7_final', 'step_7_final_approval__strategy_check', 'step_7_final_approval_strategy_check'],
  8: ['step_8_publication'],
  9: ['step_9_monitoring'],
};

function getPersistedStepContent(contextMemory, stepOrder) {
  const keys = PERSISTED_STEP_CONTENT_KEYS[stepOrder] || [];
  for (const key of keys) {
    const value = contextMemory?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function normalizeTimestamp(value, fallback) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : fallback;
}

function buildPersistedChatHistory(instance) {
  if (!instance) return [];

  const contextMemory = instance.context_memory || {};
  const workflowInstanceId = instance.id;
  const messages = [];
  let order = 0;
  const nextTs = () => Date.now() + order++;

  const pushMessage = (message) => {
    messages.push({
      workflowInstanceId,
      __selectedInstanceId: workflowInstanceId,
      ...message,
      ts: message.ts || nextTs(),
    });
  };

  if (Array.isArray(contextMemory.scribe_conversation)) {
    contextMemory.scribe_conversation.forEach((entry, index) => {
      const content = (entry?.content || '').trim();
      if (!content) return;
      const isUser = entry?.role === 'user';
      pushMessage({
        id: `persisted-scribe-${workflowInstanceId}-${index}`,
        kind: isUser ? 'user_feedback' : 'agent_message',
        agent: isUser ? 'You' : 'The Scribe',
        content,
      });
    });
  }

  for (let stepOrder = 1; stepOrder <= 9; stepOrder += 1) {
    const content = getPersistedStepContent(contextMemory, stepOrder);
    if (!content) continue;

    pushMessage({
      id: `persisted-step-${workflowInstanceId}-${stepOrder}`,
      kind: 'step_started',
      agent: STEP_AGENTS[stepOrder] || 'Agent',
      stepOrder,
      stepName: STEP_NAMES[stepOrder] || '',
    });

    pushMessage({
      id: `persisted-agent-${workflowInstanceId}-${stepOrder}`,
      kind: 'agent_message',
      agent: STEP_AGENTS[stepOrder] || 'Agent',
      stepOrder,
      stepName: STEP_NAMES[stepOrder] || '',
      content,
    });
  }

  if (Array.isArray(contextMemory.feedback_history)) {
    contextMemory.feedback_history.forEach((entry, index) => {
      const content = (entry?.message || '').trim();
      if (!content) return;
      pushMessage({
        id: `persisted-feedback-${workflowInstanceId}-${index}`,
        kind: 'user_feedback',
        agent: entry?.username && entry.username !== 'System' ? entry.username : (entry?.author || 'You'),
        content,
        stepOrder: entry?.step_order || null,
        stepName: entry?.step_name || '',
        ts: normalizeTimestamp(entry?.timestamp, nextTs()),
      });
    });
  }

  if (typeof contextMemory.qa_feedback === 'string' && contextMemory.qa_feedback.trim()) {
    pushMessage({
      id: `persisted-qa-${workflowInstanceId}`,
      kind: 'qa_failed',
      agent: 'Quality Control',
      message: contextMemory.qa_feedback.trim(),
    });
  }

  if (instance.status === 'completed') {
    pushMessage({
      id: `persisted-complete-${workflowInstanceId}`,
      kind: 'workflow_completed',
      agent: 'System',
      message: 'This project is complete. You can review the full Writers Room transcript and final document below.',
    });
  }

  return messages.slice(-300);
}


// ─── Streaming canvas text ────────────────────────────────────────────────────
function StreamingText({ text, speed = 40, onComplete }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const words = useRef([]);
  const idx   = useRef(0);
  const timer = useRef(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    words.current = (text || '').split(' ');
    idx.current = 0;
    setDisplayed('');
    setDone(false);
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (idx.current >= words.current.length) {
        clearInterval(timer.current);
        setDone(true);
        if (onCompleteRef.current) onCompleteRef.current();
        return;
      }
      const word = words.current[idx.current];
      setDisplayed(prev => (prev ? prev + ' ' + word : word));
      idx.current += 1;
    }, speed);
    return () => clearInterval(timer.current);
  }, [text, speed]);

  return (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 15, color: '#1A202C', lineHeight: 1.85 }}>
      {displayed}
      {!done && (
        <span style={{
          display: 'inline-block', width: 2, height: 15,
          background: '#1E3A8A', marginLeft: 2,
          animation: 'blink 0.8s ease-in-out infinite',
          verticalAlign: 'text-bottom',
        }} />
      )}
    </div>
  );
}

// ─── Markdown renderer ────────────────────────────────────────────────────────
function inlineFormat(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 700, color: '#0F172A' }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('# ')) return (
      <h1 key={i} style={{
        fontWeight: 900,
        fontSize: 'clamp(2rem, 7vw, 28px)',
        color: '#0F172A',
        margin: '28px 0 16px',
        fontFamily: 'Syne, sans-serif',
        lineHeight: 1.18,
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        hyphens: 'auto',
      }}>
        {line.slice(2)}
      </h1>
    );
    if (line.startsWith('## ')) return (
      <h2 key={i} style={{
        fontWeight: 800,
        fontSize: 'clamp(1.35rem, 5.4vw, 20px)',
        color: '#0F172A',
        margin: '24px 0 12px',
        fontFamily: 'Syne, sans-serif',
        lineHeight: 1.24,
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        hyphens: 'auto',
      }}>
        {line.slice(3)}
      </h2>
    );
    if (line.startsWith('### ')) return (
      <h3 key={i} style={{
        fontWeight: 600,
        fontSize: 'clamp(1rem, 4.2vw, 16px)',
        color: '#1E3A8A',
        margin: '18px 0 8px',
        lineHeight: 1.32,
        fontFamily: 'Syne, sans-serif',
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        hyphens: 'auto',
      }}>
        {line.slice(4)}
      </h3>
    );
    if (/^-{3,}$/.test(line.trim())) return (
      <hr key={i} style={{ border: 'none', borderTop: '1px solid #E2E8F0', margin: '20px 0' }} />
    );
    if (line.startsWith('- ') || line.startsWith('* ')) return (
      <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
        <span style={{ color: '#F97316', flexShrink: 0, fontWeight: 700, lineHeight: 1.7, marginTop: 1 }}>•</span>
        <span style={{ fontSize: 15, color: '#1A202C', lineHeight: 1.7, fontFamily: 'Inter, sans-serif' }}>{inlineFormat(line.slice(2))}</span>
      </div>
    );
    if (/^\d+\.\s/.test(line)) {
      const m = line.match(/^(\d+)\.\s(.*)$/);
      if (m) return (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
          <span style={{ color: '#1E3A8A', fontWeight: 700, flexShrink: 0, fontSize: 14, lineHeight: 1.7, minWidth: 20 }}>{m[1]}.</span>
          <span style={{ fontSize: 15, color: '#1A202C', lineHeight: 1.7 }}>{inlineFormat(m[2])}</span>
        </div>
      );
    }
    if (line.startsWith('**') && line.endsWith('**') && line.length > 4) return (
      <p key={i} style={{ fontWeight: 700, fontSize: 15, color: '#0F172A', margin: '14px 0 6px' }}>
        {line.slice(2, -2)}
      </p>
    );
    if (line.trim() === '') return <div key={i} style={{ height: 14 }} />;
    return (
      <p key={i} style={{ fontSize: 15, color: '#1A202C', lineHeight: 1.85, margin: '0 0 14px', fontFamily: 'Inter, sans-serif' }}>
        {inlineFormat(line)}
      </p>
    );
  });
}

// ─── Chat bubble ──────────────────────────────────────────────────────────────
function ChatBubble({ msg, completedStepOrders }) {
  const agentName = formatAgentName(msg.agent);
  const color = getAgentColor(agentName);
  const abbr  = getAgentAbbr(agentName);

  // Step divider
  if (msg.kind === 'step_started') {
    const isComplete = completedStepOrders && completedStepOrders.has(msg.stepOrder);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0 8px', userSelect: 'none' }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 12px', background: '#1E3A8A', borderRadius: 999,
        }}>
          {isComplete ? (
            <CheckCircle size={11} color="#22C55E" />
          ) : (
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              border: '2px solid #F97316', borderTopColor: 'transparent',
              animation: 'spin 0.8s linear infinite', flexShrink: 0,
            }} />
          )}
          <span style={{ fontSize: 11, color: isComplete ? '#22C55E' : 'white', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'Syne, sans-serif' }}>
            Step {msg.stepOrder}: {msg.stepName || STEP_NAMES[msg.stepOrder] || ''}
          </span>
        </div>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
      </div>
    );
  }

  // Workflow completed banner
  if (msg.kind === 'workflow_completed') {
    return (
      <div style={{
        background: 'rgba(249,115,22,0.08)',
        border: '1px solid rgba(249,115,22,0.25)', borderRadius: 10,
        padding: '12px 14px', margin: '8px 0',
        display: 'flex', alignItems: 'center', gap: 10,
        animation: 'slideUp 0.25s ease',
      }}>
        <span style={{ fontSize: 18 }}>🎉</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#F97316', marginBottom: 2, fontFamily: 'Syne, sans-serif' }}>Workflow Complete</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{msg.message}</div>
        </div>
      </div>
    );
  }

  // QA failed
  if (msg.kind === 'qa_failed') {
    return (
      <div style={{
        background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
        borderRadius: 10, padding: '10px 14px', margin: '4px 0',
        animation: 'slideUp 0.25s ease',
      }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: '#F87171', marginBottom: 2 }}>Quality Check Failed</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{msg.message}</div>
      </div>
    );
  }

  // Constitution ready
  if (msg.kind === 'constitution_ready') {
    return (
      <div style={{
        background: 'rgba(30,58,138,0.2)', border: '1px solid rgba(30,58,138,0.4)',
        borderRadius: 10, padding: '10px 14px', margin: '4px 0',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        animation: 'slideUp 0.25s ease',
      }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>📋</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#93C5FD', marginBottom: 2 }}>Constitution Ready</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{msg.message}</div>
        </div>
      </div>
    );
  }

  // Tool usage indicator
  if (msg.kind === 'tool_used') {
    const detail = compactToolDetail(msg);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px', margin: '4px 0',
        background: 'rgba(249,115,22,0.06)',
        border: '1px solid rgba(249,115,22,0.15)',
        borderRadius: 10,
        animation: 'slideUp 0.25s ease',
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: 'rgba(249,115,22,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F97316' }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#F97316', fontFamily: 'Syne, sans-serif' }}>
          {msg.agent}
        </span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          {formatToolActivityText(msg)}
        </span>
        {detail && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: '#CBD5E1',
            background: 'rgba(255,255,255,0.06)',
            padding: '2px 8px', borderRadius: 6,
          }}>
            {detail}
          </span>
        )}
      </div>
    );
  }

  // Regular agent / user message bubble
  const content = msg.content || msg.message || '';
  if (!content.trim()) return null;

  const isUser = msg.kind === 'user_feedback';
  const bubbleColor = isUser ? '#1E3A8A' : color;

  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', animation: 'slideUp 0.25s ease' }}>
      {/* Avatar */}
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: bubbleColor, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: 10, fontWeight: 800,
        boxShadow: `0 0 0 2px #111827, 0 0 0 3px ${bubbleColor}60`,
        marginTop: 2,
      }}>
        {isUser ? 'ME' : abbr}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: bubbleColor, fontFamily: 'Syne, sans-serif' }}>
            {isUser ? 'You' : agentName}
          </span>
          {msg.stepOrder != null && !isUser && (
            <span style={{
              fontSize: 10, color: 'rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: 999,
            }}>
              Step {msg.stepOrder}
            </span>
          )}
        </div>

        {/* Bubble */}
        <div style={{
          background: isUser ? 'rgba(30,58,138,0.35)' : '#1A2236',
          borderLeft: `3px solid ${bubbleColor}`,
          borderRadius: '2px 12px 12px 12px',
          padding: '10px 14px',
          fontSize: 13,
          color: '#CBD5E1',
          lineHeight: 1.65,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}>
          {content}
        </div>
        {msg.timestamp && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', marginTop: 3, paddingLeft: 2 }}>
            {formatTime(msg.timestamp)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Constitution banner ──────────────────────────────────────────────────────
function ConstitutionBanner({ constitution, instanceId, onApproved }) {
  const [expanded, setExpanded] = useState(false);
  const [revising, setRevising] = useState(false);
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);

  const approve = () => {
    setSaving(true);
    apiClient.post(`/workflow/${instanceId}/constitution/`, { action: 'approve' })
      .then(() => onApproved())
      .catch(() => setSaving(false));
  };

  const submitRevision = () => {
    if (!notes.trim()) return;
    setSaving(true);
    apiClient.post(`/workflow/${instanceId}/constitution/`, { action: 'revise', notes: notes.trim() })
      .then(() => { setRevising(false); setNotes(''); setSaving(false); })
      .catch(() => setSaving(false));
  };

  return (
    <div style={{ flexShrink: 0, borderBottom: '1px solid rgba(249,115,22,0.2)', background: 'rgba(249,115,22,0.06)', padding: '10px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: expanded ? 10 : 0 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontWeight: 700, fontSize: 12, color: '#FDBA74', flex: 1 }}>
          Constitution ready — approval required
        </span>
        <button onClick={() => setExpanded(p => !p)} style={{ fontSize: 11, color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          {expanded ? 'Hide' : 'Review'}
        </button>
        <button onClick={approve} disabled={saving} style={{ padding: '5px 14px', background: '#1E3A8A', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '…' : 'Approve'}
        </button>
        <button onClick={() => setRevising(p => !p)} style={{ padding: '5px 10px', background: 'none', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Revise
        </button>
      </div>

      {expanded && (
        <div style={{ background: '#1A2236', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 8, padding: '10px 12px', marginTop: 8, fontSize: 12, color: '#CBD5E1', lineHeight: 1.65, maxHeight: 180, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {constitution}
        </div>
      )}

      {revising && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
          <input
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="What should change in the constitution?"
            style={{ flex: 1, padding: '7px 10px', fontSize: 12, border: '1px solid rgba(249,115,22,0.3)', borderRadius: 7, outline: 'none', color: '#CBD5E1', background: '#1A2236' }}
          />
          <button onClick={submitRevision} disabled={saving} style={{ padding: '7px 14px', background: '#F97316', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Submit
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Canvas placeholder ───────────────────────────────────────────────────────
function CanvasPlaceholder({ isLive, currentStepOrder, totalSteps, currentStepName }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px 40px', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 20 }}>{isLive ? '✍️' : '📄'}</div>
      {isLive ? (
        <>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#111827', marginBottom: 8, fontFamily: 'Syne, sans-serif' }}>
            Your AI team is planning your content
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 6 }}>
            {currentStepName || `Step ${currentStepOrder || 1}`}
          </div>
          <div style={{ fontSize: 13, color: '#9CA3AF', maxWidth: 300, lineHeight: 1.65, marginBottom: 28 }}>
            The document will appear when drafting begins (Step 4)
          </div>
          <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
            {Array.from({ length: totalSteps }, (_, i) => {
              const n = i + 1;
              return (
                <div key={n} style={{
                  width: n < (currentStepOrder || 0) ? 20 : 8,
                  height: 8, borderRadius: 4,
                  background: n < (currentStepOrder || 0)
                    ? '#1E3A8A'
                    : n === (currentStepOrder || 0)
                    ? '#F97316'
                    : '#E2E8F0',
                  transition: 'all 0.4s ease',
                }} />
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#111827', marginBottom: 8, fontFamily: 'Syne, sans-serif' }}>No content yet</div>
          <div style={{ fontSize: 14, color: '#9CA3AF', maxWidth: 280 }}>
            Select a project from the tabs above to see its document.
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function TheStudio() {
  const navigate = useNavigate();
  const location = useLocation();

  const [instances, setInstances]             = useState([]);
  const [selected, setSelected]               = useState(null);
  const [wsStatus, setWsStatus]               = useState(null);
  const [chatMessages, setChatMessages]       = useState([]);
  const [docContent, setDocContent]           = useState(null);
  const [streamingContent, setStreamingContent] = useState(null);
  const [isStreamingActive, setIsStreamingActive] = useState(false);
  const [canvasFading, setCanvasFading]       = useState(false);
  const [canvasKey, setCanvasKey]             = useState(0);
  const [completedStepOrders, setCompletedStepOrders] = useState(new Set());
  const [constitution, setConstitution]       = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [constitutionApproved, setConstitutionApproved] = useState(false);
  const [isLive, setIsLive]                   = useState(false);
  const [currentAgent, setCurrentAgent]       = useState(null);
  const [currentStepOrder, setCurrentStepOrder] = useState(null);
  const [currentStepName, setCurrentStepName] = useState(null);
  const [feedback, setFeedback]               = useState('');
  const [manualNotes, setManualNotes]         = useState([]);
  const [hitlRequests, setHitlRequests]       = useState({});
  const [hitlResponseDraft, setHitlResponseDraft] = useState('');
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [copied, setCopied]                   = useState(false);
  const [isMobile, setIsMobile]               = useState(window.innerWidth < 768);
  const [mobileTab, setMobileTab]             = useState('chat'); // 'chat' | 'canvas'
  const [unreadChat, setUnreadChat]           = useState(0);

  // Publishing Valet
  const [valetOpen, setValetOpen]             = useState(false);
  const [valetMessages, setValetMessages]     = useState([]);
  const [valetInput, setValetInput]           = useState('');
  const [valetLoading, setValetLoading]       = useState(false);
  const [publishData, setPublishData]         = useState(null);

  const wsRef            = useRef(null);
  const reconnectTimer   = useRef(null);
  const reconnectAttempt = useRef(0);
  const feedRef          = useRef(null);
  const canvasRef        = useRef(null);
  const connectedIdRef   = useRef(null);
  const fadeTimer        = useRef(null);
  const valetTimerRef    = useRef(null);
  const valetOpenedRef   = useRef(false); // prevent double-open
  const pollIntervalRef  = useRef(null);  // live-workflow polling
  // Refs for values needed inside stable WS callbacks
  const docContentRef    = useRef(null);
  const isStreamingRef   = useRef(false);

  useEffect(() => { docContentRef.current = docContent; }, [docContent]);
  useEffect(() => { isStreamingRef.current = isStreamingActive; }, [isStreamingActive]);
  // Stop polling when workflow finishes
  useEffect(() => {
    if (!isLive) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [isLive]);
  // Auto-fetch The Herald's opening assessment when the panel first opens
  useEffect(() => {
    if (valetOpen && valetMessages.length === 0 && selected) openValet();
  }, [valetOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const mobileTitle = selected ? getTaskTitle(selected) : '';

  useEffect(() => {
    if (selected?.context_memory?.manual_notes?.length) {
      setManualNotes(selected.context_memory.manual_notes);
    } else {
      setManualNotes([]);
    }
  }, [selected?.context_memory?.manual_notes, selected?.id]);

  const hydrateHitlRequests = useCallback((instance) => {
    const raw = instance?.context_memory?.active_hitl_requests || {};
    const entries = Object.entries(raw).map(([conversationId, req]) => [conversationId, {
      prompt: req?.prompt || 'Your AI team needs guidance.',
      stepOrder: req?.step_order || null,
      stepName: req?.step_name || '',
      status: req?.status === 'responded' ? 'resolved' : (req?.status || 'pending'),
      timeoutSeconds: req?.timeout_seconds || null,
      createdAt: req?.requested_at || Date.now(),
      resolvedAt: req?.responded_at || req?.timed_out_at || null,
      response: req?.response || '',
    }]);
    setHitlRequests(Object.fromEntries(entries));
    setHitlResponseDraft('');
  }, []);

  const selectedActiveHitlRequests = selected?.context_memory?.active_hitl_requests || null;

  useEffect(() => {
    hydrateHitlRequests({ context_memory: { active_hitl_requests: selectedActiveHitlRequests || {} } });
  }, [hydrateHitlRequests, selected?.id, selectedActiveHitlRequests]);

  const selectedStoredChatHistory = useMemo(
    () => buildPersistedChatHistory(selected),
    [selected]
  );

  useEffect(() => {
    if (!selected?.id) {
      setChatMessages([]);
      return;
    }

    setChatMessages(prev => {
      const liveMessages = prev.filter(msg => {
        const sameInstance = msg.workflowInstanceId === selected.id || msg.__selectedInstanceId === selected.id;
        return sameInstance && !String(msg.id || '').startsWith('persisted-');
      });
      return [...selectedStoredChatHistory, ...liveMessages].slice(-300);
    });
  }, [selected?.id, selectedStoredChatHistory]);

  useEffect(() => {
    setActiveConversationId(prev => {
      if (prev && hitlRequests[prev] && ['pending', 'submitted'].includes(hitlRequests[prev].status)) {
        return prev;
      }
      const next = Object.entries(hitlRequests)
        .filter(([, req]) => req.status === 'pending')
        .sort((a, b) => {
          const aTime = new Date(a[1]?.createdAt || 0).getTime() || 0;
          const bTime = new Date(b[1]?.createdAt || 0).getTime() || 0;
          return bTime - aTime;
        })[0];
      return next ? next[0] : null;
    });
  }, [hitlRequests]);

  const pushMsg = useCallback((msg) => {
    const instanceId = connectedIdRef.current || selected?.id || null;
    setChatMessages(prev => [
      ...prev,
      {
        ...msg,
        workflowInstanceId: msg.workflowInstanceId || instanceId,
        __selectedInstanceId: msg.__selectedInstanceId || instanceId,
        ts: msg.ts || Date.now(),
      },
    ].slice(-300));
  }, [selected?.id]);

  const scrollFeedToBottom = useCallback(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, []);

  const refreshStatus = useCallback((instanceId) => {
    apiClient.get(`/workflow/${instanceId}/status/`)
      .then(r => setWsStatus(r.data))
      .catch(() => {});
  }, []);

  const reloadSelected = useCallback((instanceId) => {
    return apiClient.get('/workflow-instances/')
      .then(r => {
        const list = r.data?.results || r.data || [];
        setInstances(list);
        const updated = list.find(i => i.id === instanceId);
        if (updated) {
          setSelected(updated);
          // Don't overwrite canvas while streaming
          if (!isStreamingRef.current) {
            const content = getBestContent(updated);
            if (content) setDocContent(content);
          }
          const constitutionText = updated.context_memory?.constitution || null;
          const backendApproved = updated.context_memory?.constitution_approved === true;
          const modeRequiresApproval = ['ASSISTED', 'MANUAL'].includes(updated.user_mode);
          setConstitution(constitutionText);
          setConstitutionApproved(backendApproved);
          setRequiresApproval(modeRequiresApproval && !backendApproved);
          setIsLive(updated.status === 'in_progress');
          const done = Object.keys(updated.completed_steps || {}).length;
          setCurrentAgent(updated.status === 'in_progress' ? (STEP_AGENTS[done + 1] || null) : null);
        }
        return updated || null;
      })
      .catch(() => null);
  }, []);

  const startPollingInstance = useCallback((instanceId) => {
    if (!instanceId) return;
    clearInterval(pollIntervalRef.current);
    reloadSelected(instanceId);
    refreshStatus(instanceId);
    pollIntervalRef.current = setInterval(() => {
      reloadSelected(instanceId);
      refreshStatus(instanceId);
    }, 4000);
  }, [refreshStatus, reloadSelected]);

  // Stream content to canvas — with optional fade-out first (steps 6/7)
  const streamToCanvas = useCallback((content, stepOrder) => {
    const withFade = stepOrder >= 6 && !!docContentRef.current;
    if (withFade) {
      setCanvasFading(true);
      clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => {
        setDocContent(null);
        setStreamingContent(content);
        setIsStreamingActive(true);
        setCanvasKey(k => k + 1);
        setCanvasFading(false);
      }, 300);
    } else {
      setStreamingContent(content);
      setIsStreamingActive(true);
      setCanvasKey(k => k + 1);
    }
  }, []);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const connectWS = useCallback((instanceId) => {
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); wsRef.current = null; }

    connectedIdRef.current = instanceId;
    const WS_BASE = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
    const ws = new WebSocket(`${WS_BASE}/ws/workflow/${instanceId}/`);

    ws.onopen = () => { reconnectAttempt.current = 0; };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        const type = data.type || '';
        const now  = Date.now();

        if (type === 'constitution.ready') {
          setConstitution(data.constitution || '');
          setRequiresApproval(!!data.requires_approval);
          setConstitutionApproved(!data.requires_approval);
          pushMsg({
            id: now, kind: 'constitution_ready', agent: 'Content Strategist',
            message: data.requires_approval
              ? 'Constitution ready — your approval is required before writing begins.'
              : 'Constitution generated — writing begins shortly.',
          });

        } else if (type === 'workflow.step_started') {
          const agent = data.lead_agent ? formatAgentName(data.lead_agent) : (STEP_AGENTS[data.step_order] || 'Agent');
          const stepName = data.step_name || STEP_NAMES[data.step_order] || '';
          setCurrentAgent(agent);
          setCurrentStepOrder(data.step_order);
          setCurrentStepName(stepName);
          setIsLive(true);
          pushMsg({ id: now, kind: 'step_started', agent, stepOrder: data.step_order, stepName });
          refreshStatus(connectedIdRef.current);

        } else if (type === 'agent.message') {
          // Skip Orchestrator — it's an empty proxy turn
          if (data.agent === 'Orchestrator') return;
          const agent   = formatAgentName(data.agent || 'Agent');
          const content = (data.content || '').trim();
          if (content.length > 0) {
            pushMsg({
              id: now, kind: 'agent_message', agent,
              stepOrder: data.step_order, stepName: data.step_name,
              content,
            });
            // Track unread when user is viewing the canvas on mobile
            setMobileTab(prev => {
              if (prev === 'canvas') setUnreadChat(u => u + 1);
              return prev;
            });
          }

        } else if (type === 'agent.content' || type === 'agent.output') {
          const stepOrder = data.step_order || 0;
          const content   = (data.content || data.output || '').trim();
          // Update canvas for steps 4, 6, 7 only
          if (content.length > 50 && stepOrder >= 4) {
            streamToCanvas(content, stepOrder);
            // Auto-switch to canvas on mobile when drafting starts
            setIsMobile(m => { if (m) setMobileTab('canvas'); return m; });
          }

        } else if (type === 'agent.update') {
          if (data.agent_name === 'Orchestrator' || data.agent === 'Orchestrator') return;
          const agent = formatAgentName(data.agent_name || data.agent || 'Agent');
          const text  = (data.message || data.output_preview || '').trim();
          if (text.length > 30) {
            pushMsg({ id: now, kind: 'agent_update', agent, stepOrder: data.step_order, content: text.substring(0, 400) });
          }

        } else if (type === 'workflow.step_completed') {
          const stepOrder = data.step_order;
          setCompletedStepOrders(prev => new Set([...prev, stepOrder]));
          reloadSelected(connectedIdRef.current);
          refreshStatus(connectedIdRef.current);

        } else if (type === 'workflow.completed') {
          setIsLive(false);
          setCurrentAgent(null);
          setCurrentStepName(null);
          const fc = data.final_content || data.final_content_preview || '';
          if (fc && !isStreamingRef.current) setDocContent(fc);
          pushMsg({
            id: now, kind: 'workflow_completed', agent: 'System',
            message: 'All steps complete. Your content is ready in the canvas →',
          });
          // Auto-switch to canvas on mobile when workflow finishes
          setIsMobile(m => { if (m) setMobileTab('canvas'); return m; });
          reloadSelected(connectedIdRef.current);
          // Auto-open Publishing Valet 2s after workflow completes
          if (!valetOpenedRef.current) {
            valetOpenedRef.current = true;
            valetTimerRef.current = setTimeout(() => setValetOpen(true), 2000);
          }

        } else if (type === 'workflow.qa_failed') {
          pushMsg({
            id: now, kind: 'qa_failed', agent: 'Quality Control',
            message: `Quality check failed — returning to editing.${data.reason ? ' ' + data.reason : ''}`,
          });

        } else if (type === 'workflow.awaiting_approval') {
          pushMsg({
            id: now, kind: 'step_started', agent: 'System',
            stepOrder: data.step_order,
            stepName: data.step_name || STEP_NAMES[data.step_order] || '',
          });
        } else if (type === 'workflow.user_feedback') {
          const text = (data.message || '').trim();
          if (text.length) {
            const agentLabel = data.username && data.username !== 'System' ? data.username : 'You';
            pushMsg({ id: now, kind: 'user_feedback', agent: agentLabel, content: text });
          }
        } else if (type === 'workflow.hitl_request') {
          console.log('[HITL] workflow.hitl_request received', data);
          const conversationId = data.conversation_id;
          if (conversationId) {
            setHitlRequests(prev => ({
              ...prev,
              [conversationId]: {
                prompt: data.prompt || 'Your AI team needs guidance.',
                stepOrder: data.step_order || null,
                stepName: data.step_name || '',
                status: 'pending',
                timeoutSeconds: data.timeout_seconds || null,
                createdAt: data.requested_at || Date.now(),
              },
            }));
          }
          pushMsg({
            id: now,
            kind: 'system',
            agent: 'System',
            message: 'Agents paused for your feedback.',
          });
        } else if (type === 'workflow.hitl_response_received') {
          console.log('[HITL] workflow.hitl_response_received received', data);
          const conversationId = data.conversation_id;
          if (conversationId) {
            setHitlRequests(prev => {
              if (!prev[conversationId]) return prev;
              return {
                ...prev,
                [conversationId]: {
                  ...prev[conversationId],
                  status: 'resolved',
                  response: data.response || '',
                  resolvedAt: data.timestamp || Date.now(),
                },
              };
            });
          }
        } else if (type === 'workflow.hitl_timeout') {
          console.warn('[HITL] workflow.hitl_timeout received', data);
          const conversationId = data.conversation_id;
          if (conversationId) {
            setHitlRequests(prev => {
              if (!prev[conversationId]) return prev;
              return {
                ...prev,
                [conversationId]: {
                  ...prev[conversationId],
                  status: 'timeout',
                  resolvedAt: Date.now(),
                },
              };
            });
          }
          pushMsg({
            id: now,
            kind: 'system',
            agent: 'System',
            message: 'Request for feedback timed out — agents resumed automatically.',
          });
        } else if (type === 'workflow.notes_updated') {
          setManualNotes(data.notes || []);
        } else if (type === 'workflow.step_rerun') {
          pushMsg({
            id: now,
            kind: 'system',
            agent: 'System',
            message: `Rerunning Step ${data.step_order}: ${data.step_name || ''}`,
          });
        } else if (type === 'workflow.error') {
          setIsLive(false);
          pushMsg({
            id: now,
            kind: 'system',
            agent: 'System',
            message: `Workflow error: ${data.error || 'Unknown error'}. The workflow has been stopped.`,
          });
        } else if (type === 'tool.used') {
          const toolMsg = {
            id: now,
            kind: 'tool_used',
            agent: data.agent || 'Agent',
            tool: data.tool || 'web_search',
            status: data.status || 'completed',
            query: data.query || '',
            target: data.target || '',
            detail: data.detail || '',
            stepOrder: data.step_order,
            toolCallId: data.tool_call_id || '',
          };
          if (data.tool_call_id && data.status !== 'started') {
            setChatMessages(prev => prev.map(m =>
              m.kind === 'tool_used' && m.toolCallId === data.tool_call_id && m.status === 'started'
                ? toolMsg
                : m
            ));
          } else {
            pushMsg(toolMsg);
          }
        }
      } catch { /* ignore malformed frames */ }
    };

    ws.onclose = () => {
      if (connectedIdRef.current !== instanceId) return;
      reconnectAttempt.current += 1;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current - 1), 30000);
      reconnectTimer.current = setTimeout(() => {
        if (connectedIdRef.current === instanceId) connectWS(instanceId);
      }, delay);
    };

    wsRef.current = ws;
  }, [pushMsg, refreshStatus, reloadSelected, streamToCanvas]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    apiClient.get('/workflow-instances/')
      .then(r => {
        const list = r.data?.results || r.data || [];
        setInstances(list);
        setLoading(false);
        if (!list.length) return;

        const navId = location.state?.workflowInstanceId;
        let target = navId ? list.find(i => i.id === navId) : null;
        if (!target) target = list.find(i => i.status === 'in_progress') || list[0];

        if (target) {
          setSelected(target);
          const content = getBestContent(target);
          if (content) setDocContent(content);
          console.log(
            '[Studio] Loaded instance', target?.id,
            '| status:', target?.status,
            '| content:', content?.length, 'chars',
            '| preview:', content?.substring(0, 60)
          );
          setIsLive(target.status === 'in_progress');
          const done = Object.keys(target.completed_steps || {}).length;
          setCurrentAgent(target.status === 'in_progress' ? (STEP_AGENTS[done + 1] || null) : null);
          setCurrentStepOrder(done + 1);
          setCurrentStepName(STEP_NAMES[done + 1] || null);
          const completedKeys = Object.keys(target.completed_steps || {}).map(k => parseInt(k, 10));
          if (completedKeys.length) setCompletedStepOrders(new Set(completedKeys));
          const constitutionText = target.context_memory?.constitution || null;
          const backendApproved = target.context_memory?.constitution_approved === true;
          const modeRequiresApproval = ['ASSISTED', 'MANUAL'].includes(target.user_mode);
          setConstitution(constitutionText);
          setConstitutionApproved(backendApproved);
          setRequiresApproval(modeRequiresApproval && !backendApproved);
          // Trigger Herald for completed instances loaded from API
          if (target.status === 'completed' && content) {
            if (!valetOpenedRef.current) {
              valetOpenedRef.current = true;
              valetTimerRef.current = setTimeout(() => setValetOpen(true), 2000);
            }
          }
          // Poll every 4s while live to show progress
          if (target.status === 'in_progress') {
            startPollingInstance(target.id);
          }
          refreshStatus(target.id);
          connectWS(target.id);
        }
      })
      .catch(() => setLoading(false));

    return () => {
      connectedIdRef.current = null;
      clearTimeout(reconnectTimer.current);
      clearTimeout(fadeTimer.current);
      clearTimeout(valetTimerRef.current);
      clearInterval(pollIntervalRef.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat on new messages
  useEffect(() => {
    scrollFeedToBottom();
  }, [chatMessages.length, scrollFeedToBottom]);

  // ── Switch instance ───────────────────────────────────────────────────────
  const switchInstance = (inst) => {
    clearTimeout(fadeTimer.current);
    clearTimeout(valetTimerRef.current);
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
    valetOpenedRef.current = false;
    setValetOpen(false);
    setValetMessages([]);
    setChatMessages([]);
    const content = getBestContent(inst);
    setDocContent(content);
    setStreamingContent(null);
    setIsStreamingActive(false);
    setCanvasFading(false);
    setCompletedStepOrders(new Set(Object.keys(inst.completed_steps || {}).map(k => parseInt(k, 10))));
    setSelected(inst);
    setIsLive(inst.status === 'in_progress');
    const done = Object.keys(inst.completed_steps || {}).length;
    setCurrentAgent(inst.status === 'in_progress' ? (STEP_AGENTS[done + 1] || null) : null);
    setCurrentStepOrder(done + 1);
    setCurrentStepName(STEP_NAMES[done + 1] || null);
    const ec = inst.context_memory?.constitution || null;
    const backendApproved = inst.context_memory?.constitution_approved === true;
    const modeRequiresApproval = ['ASSISTED', 'MANUAL'].includes(inst.user_mode);
    setConstitution(ec);
    setConstitutionApproved(backendApproved);
    setRequiresApproval(modeRequiresApproval && !backendApproved);
    // Trigger Herald when switching to a completed instance
    if (inst.status === 'completed' && content) {
      valetOpenedRef.current = true;
      valetTimerRef.current = setTimeout(() => setValetOpen(true), 2000);
    }
    // Restart polling if switching to a live instance
    if (inst.status === 'in_progress') {
      startPollingInstance(inst.id);
    }
    refreshStatus(inst.id);
    connectWS(inst.id);
  };

  // ── Refresh canvas from API ───────────────────────────────────────────────
  const handleRefresh = useCallback(() => {
    if (!selected) return;
    apiClient.get('/workflow-instances/')
      .then(r => {
        const list = r.data?.results || r.data || [];
        setInstances(list);
        const updated = list.find(i => i.id === selected.id);
        if (updated) {
          setSelected(updated);
          const content = getBestContent(updated);
          if (content) setDocContent(content);
        }
      })
      .catch(() => {});
  }, [selected]);

  // ── Feedback ──────────────────────────────────────────────────────────────
  const userMode = selected?.user_mode || 'FULL_AUTOMATION';
  const canSendFeedback = userMode === 'ASSISTED' || userMode === 'MANUAL';
  const isManualMode = userMode === 'MANUAL';
  const currentHitlRequest = activeConversationId ? hitlRequests[activeConversationId] : null;
  const pendingHitlCount = Object.values(hitlRequests).filter(req => req.status === 'pending').length;

  const sendFeedback = () => {
    if (!feedback.trim() || !canSendFeedback || !selected) return;
    const payload = {
      type: 'user.feedback',
      message: feedback.trim(),
      instance_id: selected.id,
    };
    const socket = wsRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    } else {
      pushMsg({ id: Date.now(), kind: 'user_feedback', agent: 'You', content: feedback.trim() });
    }
    setFeedback('');
  };

  const handleConstitutionApproved = useCallback(async () => {
    if (!selected) return;
    setConstitutionApproved(true);
    setRequiresApproval(false);
    await reloadSelected(selected.id);
    startPollingInstance(selected.id);
  }, [reloadSelected, selected, startPollingInstance]);

  const sendHitlResponse = async () => {
    if (!selected || !currentHitlRequest || !hitlResponseDraft.trim()) return;
    const payload = {
      conversation_id: activeConversationId,
      response: hitlResponseDraft.trim(),
    };
    console.log('[HITL] Submitting HITL response', payload);
    try {
      await apiClient.post(`/workflow/${selected.id}/hitl-response/`, payload);
      const socket = wsRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'workflow.hitl_response', ...payload }));
      }
      setHitlRequests(prev => {
        if (!activeConversationId || !prev[activeConversationId]) return prev;
        return {
          ...prev,
          [activeConversationId]: {
            ...prev[activeConversationId],
            status: 'submitted',
            response: payload.response,
          },
        };
      });
      setHitlResponseDraft('');
      refreshStatus(selected.id);
      reloadSelected(selected.id);
    } catch (err) {
      console.error('[HITL] Failed to submit response', err);
    }
  };

  const pinManualNote = async () => {
    if (!isManualMode || !selected) return;
    const note = window.prompt('Add a note for your AI team:');
    if (!note || !note.trim()) return;
    try {
      const res = await apiClient.post(`/workflow/${selected.id}/manual-notes/`, { note: note.trim() });
      setManualNotes(res.data?.notes || []);
    } catch (err) {
      console.error(err);
    }
  };

  const rerunCurrentStep = async () => {
    if (!isManualMode || !selected || !currentStepOrder) return;
    try {
      await apiClient.post(`/workflow/${selected.id}/steps/${currentStepOrder}/rerun/`);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Copy / Download ───────────────────────────────────────────────────────
  const copyContent = () => {
    navigator.clipboard.writeText(docContent || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const downloadContent = () => {
    const blob = new Blob([docContent || ''], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${(getTaskTitle(selected) || 'document').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Publishing Valet ───────────────────────────────────────────────────────
  const openValet = async () => {
    setValetOpen(true);
    if (valetMessages.length > 0 || !selected) return; // already started
    setValetLoading(true);
    try {
      const res = await apiClient.post('/publishing-valet/', {
        instance_id: selected.id,
        messages: [],
        message: '',
      });
      const { reply, is_complete, publish_data } = res.data;
      setValetMessages([{ role: 'assistant', content: reply }]);
      if (is_complete && publish_data) setPublishData(publish_data);
    } catch {
      setValetMessages([{ role: 'assistant', content: "I am The Herald. Your piece is ready. Where are you planning to publish it?" }]);
    } finally {
      setValetLoading(false);
    }
  };

  const sendValetMessage = async () => {
    const text = valetInput.trim();
    if (!text || valetLoading || !selected) return;
    const newMsg = { role: 'user', content: text };
    const updated = [...valetMessages, newMsg];
    setValetMessages(updated);
    setValetInput('');
    setValetLoading(true);
    try {
      const history = updated.slice(0, -1).map(m => ({ role: m.role, content: m.content }));
      const res = await apiClient.post('/publishing-valet/', {
        instance_id: selected.id,
        messages: history,
        message: text,
      });
      const { reply, is_complete, publish_data } = res.data;
      setValetMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      if (is_complete && publish_data) setPublishData(publish_data);
    } catch {
      setValetMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I had a technical issue. Please try again." }]);
    } finally {
      setValetLoading(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const completedStepsObj = selected?.completed_steps || wsStatus?.completed_steps || {};
  const doneCount  = Object.keys(completedStepsObj).length;
  const taskFormat = getTaskFormat(selected);
  const taskStyle = selected?.context_memory?.writing_style
    || selected?.context_memory?.intake_brief?.writing_style
    || getTaskStyle(selected);
  const totalSteps = FORMAT_STEP_COUNT[taskFormat] || 9;
  const progress   = totalSteps > 0 ? Math.round((doneCount / totalSteps) * 100) : 0;
  const wordCount  = docContent ? docContent.split(/\s+/).filter(Boolean).length : 0;
  const totalTokens    = getTotalTokens(selected);
  const estimatedCost  = selected?.context_memory?.estimated_cost_usd;

  // Canvas: show article content when available, or while streaming
  const showCanvas   = !!docContent || (isStreamingActive && !!streamingContent);
  const showStreaming = isStreamingActive && !!streamingContent;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0D1117', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ fontSize: 14, color: '#6B7280' }}>Loading Studio…</div>
      </div>
    );
  }

  if (!instances.length) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0D1117', fontFamily: 'Inter, sans-serif', gap: 16 }}>
        <div style={{ fontSize: 48 }}>✍️</div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: 'white' }}>No projects yet</div>
        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 8 }}>Create a project to see your AI writing team in action.</div>
        <button
          onClick={() => navigate('/task-creation')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', background: '#F97316', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, fontFamily: 'Syne, sans-serif', cursor: 'pointer' }}
        >
          <Plus size={18} /> Create Project
        </button>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'Inter, sans-serif', background: '#0D1117' }}>
      <style>{`
        @keyframes blink   { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
      `}</style>

      {/* ── TOP BAR ── */}
      <div style={{ height: 52, background: '#111827', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', padding: isMobile ? '0 8px' : '0 12px', gap: isMobile ? 5 : 8, flexShrink: 0, zIndex: 10 }}>
        {/* Back button - always visible */}
        <button
          onClick={() => navigate('/dashboard')}
          title="Back to Pulse"
          style={{
            width: isMobile ? 32 : 36, height: isMobile ? 32 : 36, borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(249,115,22,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
        >
          <ArrowLeft size={isMobile ? 16 : 18} color="#9CA3AF" />
        </button>

        {/* Logo - clickable to dashboard */}
        {!isMobile && (
          <div
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}
          >
            <Logo size={26} />
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 14, color: 'white', letterSpacing: '-0.01em' }}>
              Studio
            </span>
          </div>
        )}

        {!isMobile && <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />}

        {/* Project tabs - desktop only */}
        {!isMobile && (
          <div style={{ flex: 1, display: 'flex', gap: 5, overflowX: 'auto', alignItems: 'center', scrollbarWidth: 'none' }}>
            {instances.slice(0, 12).map(inst => {
              const isSel = selected?.id === inst.id;
              const t = getTaskTitle(inst);
              return (
                <button
                  key={inst.id}
                  onClick={() => switchInstance(inst)}
                  style={{
                    flexShrink: 0, height: 'auto', padding: '3px 11px', borderRadius: 10,
                    border: 'none',
                    background: isSel ? '#F97316' : 'transparent',
                    color: isSel ? 'white' : '#6B7280',
                    fontSize: 12, fontWeight: isSel ? 700 : 500,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{
                      width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                      background: isSel ? 'rgba(255,255,255,0.8)' : inst.status === 'in_progress' ? '#F97316' : inst.status === 'completed' ? '#22C55E' : '#4B5563',
                    }} />
                    {t.length > 24 ? t.substring(0, 24) + '…' : t}
                  </div>
                  {inst.updated_at && (
                    <span style={{ fontSize: 9, opacity: 0.65, paddingLeft: 10 }}>
                      {timeAgo(inst.updated_at)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Mobile: show current project title */}
        {isMobile && selected && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              maskImage: 'none',
              WebkitMaskImage: 'none',
            }}
          >
            <div
              title={mobileTitle}
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'white',
                whiteSpace: 'nowrap',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                letterSpacing: '-0.01em',
              }}
            >
              {mobileTitle}
            </div>
          </div>
        )}

        {/* Right side: status + cost + new + nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 8, flexShrink: 0 }}>
          {selected && !isMobile && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 5,
              fontSize: 11,
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: 999,
              background: isLive ? 'rgba(249,115,22,0.15)' : selected?.status === 'completed' ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.06)',
              color: isLive ? '#F97316' : selected?.status === 'completed' ? '#22C55E' : '#6B7280',
              flexShrink: 0,
            }}>
              {isLive && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#F97316', animation: 'pulse 1.4s infinite' }} />}
              {isLive ? 'LIVE' : selected?.status === 'completed' ? 'Complete' : 'Ready'}
            </span>
          )}
          {estimatedCost != null && !isMobile && (
            <span style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3 }}>⚡ ~${estimatedCost}</span>
          )}
          {/* New button - desktop only (mobile has it in bottom bar) */}
          {!isMobile && (
            <button
              onClick={() => navigate('/task-creation')}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: '#F97316', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: 'Syne, sans-serif', cursor: 'pointer' }}
            >
              <Plus size={12} /> New
            </button>
          )}
          {/* Nav icons - desktop only */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
              {[
                { icon: Activity, path: '/dashboard', label: 'Pulse' },
                { icon: PenTool, path: '/studio', label: 'Studio' },
                { icon: Archive, path: '/vault', label: 'Vault' },
              ].map(({ icon: Icon, path, label }) => {
                const isAct = location.pathname === path;
                return (
                  <button key={path} onClick={() => navigate(path)} title={label} style={{ width: 32, height: 32, borderRadius: 7, background: isAct ? 'rgba(249,115,22,0.15)' : 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} color={isAct ? '#F97316' : '#6B7280'} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── PROGRESS BAR ── */}
      <div style={{ height: 3, background: 'rgba(255,255,255,0.05)', flexShrink: 0 }}>
        <div style={{
          height: '100%',
          width: `${Math.max(progress, doneCount > 0 ? 5 : 0)}%`,
          background: 'linear-gradient(90deg, #F97316, #EA580C)',
          transition: 'width 0.8s ease',
        }} />
      </div>

      {/* ── MAIN SPLIT PANE ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── LEFT: Group Chat ── */}
        <div style={{
          width: isMobile ? '100%' : '45%',
          display: isMobile && mobileTab !== 'chat' ? 'none' : 'flex',
          flexDirection: 'column',
          borderRight: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
          background: '#111827',
          overflow: 'hidden',
        }}>

          {/* Panel header */}
          <div style={{ height: 48, flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'white', fontFamily: 'Syne, sans-serif' }}>Writers Room</span>
            {isLive && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#F97316', fontWeight: 700, background: 'rgba(249,115,22,0.12)', padding: '2px 8px', borderRadius: 999 }}>
                <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#F97316', animation: 'pulse 1.4s infinite' }} />
                LIVE
              </div>
            )}
            {chatMessages.filter(m => m.kind === 'agent_message').length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6B7280', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 999 }}>
                {chatMessages.filter(m => m.kind === 'agent_message').length} msgs
              </span>
            )}
          </div>

          {/* Constitution banner (ASSISTED mode) */}
          {constitution && requiresApproval && !constitutionApproved && selected && (
            <ConstitutionBanner
              constitution={constitution}
              instanceId={selected.id}
              onApproved={handleConstitutionApproved}
            />
          )}

          {currentHitlRequest && (
            <div style={{ padding: '8px 12px', background: 'rgba(30,58,138,0.15)', borderBottom: '1px solid rgba(30,58,138,0.35)', fontSize: 12, color: '#E0E7FF', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 600 }}>Agents are waiting on Step {currentHitlRequest.stepOrder || '?'}{currentHitlRequest.stepName ? ` · ${currentHitlRequest.stepName}` : ''}</div>
              <div>{currentHitlRequest.prompt}</div>
              {pendingHitlCount > 1 && (
                <div style={{ fontSize: 11, color: '#CBD5F5' }}>+{pendingHitlCount - 1} more request(s) queued</div>
              )}
            </div>
          )}

          {isManualMode && (
            <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(17,24,39,0.6)', color: '#CBD5E1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 12 }}>Manual Notes</span>
                <button onClick={pinManualNote} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid rgba(249,115,22,0.6)', background: 'rgba(249,115,22,0.15)', color: '#FDBA74', cursor: 'pointer' }}>
                  Pin Note
                </button>
                <button onClick={rerunCurrentStep} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid rgba(30,58,138,0.6)', background: 'rgba(30,58,138,0.15)', color: '#BFDBFE', cursor: 'pointer' }}>
                  Rerun Step
                </button>
              </div>
              {manualNotes.length > 0 ? (
                <div style={{ maxHeight: 100, overflowY: 'auto', fontSize: 11, lineHeight: 1.5 }}>
                  {manualNotes.slice(-5).reverse().map((note, idx) => (
                    <div key={`${note.timestamp}-${idx}`} style={{ marginBottom: 4, color: '#E5E7EB' }}>
                      <span style={{ color: '#94A3B8' }}>{note.author || 'User'}:</span> {note.note}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#94A3B8' }}>No notes yet.</div>
              )}
            </div>
          )}

          {/* Scrollable chat feed */}
          <div ref={feedRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 12px 8px' }}>
            {chatMessages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#4B5563', padding: '60px 20px', fontSize: 13 }}>
                {isLive
                  ? 'Agents are starting up — conversations will appear here…'
                  : selected?.status === 'completed'
                  ? 'This project is complete.'
                  : 'No activity yet. Start a workflow to see your agents collaborate.'}
              </div>
            ) : (
              chatMessages.map(msg => (
                <ChatBubble key={msg.id} msg={msg} completedStepOrders={completedStepOrders} />
              ))
            )}
          </div>

          {/* Feedback input */}
          <div style={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 12px', background: '#1A2236' }}>
            {isLive && currentAgent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, paddingLeft: 2 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#F97316', animation: 'pulse 1.4s infinite' }} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{currentAgent} is working…</span>
              </div>
            )}
            {canSendFeedback ? (
              currentHitlRequest ? (
                <>
                  <div style={{ fontSize: 11, color: '#FCD34D', marginBottom: 6 }}>
                    Agents paused on Step {currentHitlRequest.stepOrder || '?'}: respond to the prompt above to continue.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '9px 12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <User size={12} color="#4B5563" />
                      <input
                        value={hitlResponseDraft}
                        onChange={e => setHitlResponseDraft(e.target.value)}
                        onKeyDown={e => (e.key === 'Enter' && !e.shiftKey) && sendHitlResponse()}
                        placeholder="Type your guidance… (Enter to send)"
                        style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#E2E8F0', width: '100%' }}
                      />
                    </div>
                    <button
                      onClick={sendHitlResponse}
                      style={{ width: 38, height: 38, borderRadius: 10, background: '#F97316', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      <Send size={13} color="white" />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '9px 12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <User size={12} color="#4B5563" />
                    <input
                      value={feedback}
                      onChange={e => setFeedback(e.target.value)}
                      onKeyDown={e => (e.key === 'Enter' && !e.shiftKey) && sendFeedback()}
                      placeholder="Send feedback to agents… (Enter to send)"
                      style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#E2E8F0', width: '100%' }}
                    />
                  </div>
                  <button
                    onClick={sendFeedback}
                    style={{ width: 38, height: 38, borderRadius: 10, background: '#F97316', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >
                    <Send size={13} color="white" />
                  </button>
                </div>
              )
            ) : (
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                Feedback available in Assisted or Manual mode.
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Document Canvas ── */}
        <div style={{
          flex: 1,
          width: isMobile ? '100%' : undefined,
          display: isMobile && mobileTab !== 'canvas' ? 'none' : 'flex',
          flexDirection: 'column',
          background: '#0D1117',
          overflow: 'hidden',
        }}>

          {/* Canvas header */}
          <div style={{
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            padding: isMobile ? '7px 12px' : '0 20px',
            gap: isMobile ? 5 : 10,
            background: '#111827',
            minHeight: isMobile ? undefined : 48,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              minWidth: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'nowrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#F1F5F9', flexShrink: 0 }}>Document</span>
                {!isLive && selected?.status === 'completed' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <CheckCircle size={12} color="#16A34A" />
                    <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 600 }}>Ready</span>
                  </div>
                )}
                {isLive && (
                  <span style={{ fontSize: 11, color: '#F97316', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#F97316', animation: 'pulse 1.4s infinite' }} />
                    {(currentStepOrder || 0) >= 4 ? 'Streaming' : 'Planning'}
                  </span>
                )}
              </div>

              {!isMobile && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button
                    onClick={handleRefresh}
                    title="Reload content from server"
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, color: '#9CA3AF', background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}
                  >
                    <RefreshCw size={12} /> Refresh
                  </button>
                  {docContent && (
                    <>
                      <button
                        onClick={copyContent}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, color: copied ? '#16A34A' : '#9CA3AF', background: copied ? 'rgba(22,163,74,0.12)' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}
                      >
                        <Copy size={12} /> {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={downloadContent}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', fontSize: 12, color: '#9CA3AF', background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}
                      >
                        <Download size={12} /> Save
                      </button>
                    </>
                  )}
                </div>
              )}

              {isMobile && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                }}>
                  <button
                    onClick={handleRefresh}
                    title="Reload content from server"
                    aria-label="Refresh document"
                    style={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#E5E7EB',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 9,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <RefreshCw size={13} />
                  </button>
                  {docContent && (
                    <>
                      <button
                        onClick={copyContent}
                        title={copied ? 'Copied' : 'Copy'}
                        aria-label={copied ? 'Copied' : 'Copy document'}
                        style={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: copied ? '#16A34A' : '#E5E7EB',
                          background: copied ? 'rgba(22,163,74,0.12)' : 'rgba(255,255,255,0.05)',
                          border: copied ? '1px solid rgba(22,163,74,0.35)' : '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 9,
                          cursor: 'pointer',
                          flexShrink: 0,
                          transition: 'all 0.15s',
                        }}
                      >
                        <Copy size={13} />
                      </button>
                      <button
                        onClick={downloadContent}
                        title="Save document"
                        aria-label="Save document"
                        style={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#E5E7EB',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 9,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Download size={13} />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 5 : 6,
              flexWrap: 'nowrap',
              overflowX: isMobile ? 'auto' : 'visible',
              scrollbarWidth: 'none',
              minWidth: 0,
              paddingBottom: isMobile ? 1 : 0,
            }}>
              {taskFormat && taskFormat !== 'Content' && (
                <span style={{
                  fontSize: isMobile ? 10 : 11,
                  color: '#E2E8F0',
                  background: 'rgba(249,115,22,0.12)',
                  border: '1px solid rgba(249,115,22,0.22)',
                  padding: isMobile ? '2px 7px' : '2px 8px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {taskFormat}
                </span>
              )}
              {taskStyle && (
                <span style={{
                  fontSize: isMobile ? 10 : 11,
                  color: '#BFDBFE',
                  background: 'rgba(30,58,138,0.13)',
                  border: '1px solid rgba(96,165,250,0.16)',
                  padding: isMobile ? '2px 7px' : '2px 8px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {taskStyle}
                </span>
              )}
              {!isMobile && wordCount > 0 && (
                <span style={{ fontSize: 11, color: '#9CA3AF', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {wordCount.toLocaleString()} words
                </span>
              )}
              {!isMobile && totalTokens > 0 && (
                <span style={{
                  fontSize: 11, color: '#9CA3AF', background: 'rgba(255,255,255,0.06)',
                  padding: '2px 8px', borderRadius: 999,
                  display: 'flex', alignItems: 'center', gap: 3,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  ⚡ ~{(totalTokens / 1000).toFixed(0)}k tokens
                  {estimatedCost != null && ` · ~$${estimatedCost}`}
                </span>
              )}
              {isMobile && wordCount > 0 && (
                <span style={{
                  fontSize: 10,
                  color: '#94A3B8',
                  background: 'rgba(255,255,255,0.06)',
                  padding: '2px 7px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  {wordCount.toLocaleString()}w
                </span>
              )}
              {isMobile && totalTokens > 0 && (
                <span style={{
                  fontSize: 10,
                  color: '#94A3B8',
                  background: 'rgba(255,255,255,0.06)',
                  padding: '2px 7px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                  ~{(totalTokens / 1000).toFixed(0)}k tok
                </span>
              )}
            </div>
          </div>

          {/* Canvas body */}
          <div ref={canvasRef} style={{ flex: 1, overflowY: 'auto', background: 'white', paddingBottom: valetOpen ? 0 : 90 }}>
            {showCanvas ? (
              <div
                key={canvasKey}
                style={{
                  maxWidth: 720, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 48px',
                  opacity: canvasFading ? 0 : 1,
                  transition: 'opacity 0.3s ease',
                  animation: canvasKey > 0 ? 'fadeIn 0.4s ease' : 'none',
                }}
              >
                {showStreaming ? (
                  <StreamingText
                    text={streamingContent}
                    speed={40}
                    onComplete={() => {
                      setDocContent(streamingContent);
                      setIsStreamingActive(false);
                    }}
                  />
                ) : (
                  renderMarkdown(docContent)
                )}
              </div>
            ) : (
              <CanvasPlaceholder
                isLive={isLive}
                currentStepOrder={currentStepOrder}
                totalSteps={totalSteps}
                currentStepName={currentStepName || STEP_NAMES[currentStepOrder] || ''}
              />
            )}
          </div>

          {/* ── Publishing Valet Panel ── */}
          {!isLive && selected?.status === 'completed' && (
            <div style={{ flexShrink: 0 }}>
              {/* Trigger button (when closed) */}
              {!valetOpen && (
                <div style={{
                  padding: '10px 16px',
                  borderTop: '1px solid #E2E8F0',
                  background: 'white',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <button
                    onClick={openValet}
                    style={{
                      flex: 1, padding: '10px 16px', borderRadius: 10,
                      background: 'linear-gradient(135deg, #0A0F1E, #1E3A8A)',
                      color: 'white', border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, display: 'flex',
                      alignItems: 'center', gap: 8, justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: 16 }}>🚀</span>
                    Chat with The Herald
                    <span style={{ fontSize: 11, opacity: 0.7, fontWeight: 500 }}>· Distribution strategy</span>
                  </button>
                </div>
              )}

              {/* Valet panel (when open) */}
              {valetOpen && (
                <div style={{
                  height: 340,
                  borderTop: '1px solid rgba(30,58,138,0.2)',
                  background: '#0A0F1E',
                  display: 'flex', flexDirection: 'column',
                  animation: 'slideUp 0.3s ease',
                }}>
                  {/* Panel header */}
                  <div style={{
                    flexShrink: 0, height: 44,
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '0 14px',
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #F97316, #1E3A8A)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 800, color: 'white',
                    }}>H</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#F1F5F9' }}>The Herald</div>
                    </div>
                    <button
                      onClick={() => setValetOpen(false)}
                      style={{
                        marginLeft: 'auto', background: 'none', border: 'none',
                        color: '#475569', fontSize: 18, cursor: 'pointer', lineHeight: 1,
                      }}
                    >×</button>
                  </div>

                  {/* Messages */}
                  <div style={{
                    flex: 1, overflowY: 'auto',
                    padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}>
                    {valetLoading && valetMessages.length === 0 && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', flexShrink: 0 }}>M</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[0, 1, 2].map(d => (
                            <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: '#64748B', animation: `pulse 1.2s ease-in-out ${d * 0.2}s infinite` }} />
                          ))}
                        </div>
                      </div>
                    )}
                    {valetMessages.map((msg, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        gap: 8, alignItems: 'flex-start',
                      }}>
                        {msg.role === 'assistant' && (
                          <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #F97316, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', marginTop: 1 }}>M</div>
                        )}
                        <div style={{
                          maxWidth: '78%',
                          padding: '8px 12px',
                          borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
                          background: msg.role === 'user'
                            ? 'rgba(30,58,138,0.5)'
                            : 'rgba(255,255,255,0.06)',
                          border: msg.role === 'assistant' ? '1px solid rgba(255,255,255,0.07)' : 'none',
                          fontSize: 12, lineHeight: 1.6, color: '#CBD5E1',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    ))}
                    {valetLoading && valetMessages.length > 0 && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #F97316, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'white', flexShrink: 0 }}>M</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {[0, 1, 2].map(d => (
                            <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: '#64748B', animation: `pulse 1.2s ease-in-out ${d * 0.2}s infinite` }} />
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Publish data card */}
                    {publishData && (
                      <div style={{
                        background: 'rgba(249,115,22,0.08)',
                        border: '1px solid rgba(249,115,22,0.25)',
                        borderRadius: 10, padding: '12px 14px',
                        marginTop: 4,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#F97316', marginBottom: 8 }}>
                          📣 Publishing Plan Ready
                        </div>
                        {publishData.headline && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: '#64748B', marginBottom: 2 }}>Headline</div>
                            <div style={{ fontSize: 12, color: '#E2E8F0', fontWeight: 600 }}>{publishData.headline}</div>
                          </div>
                        )}
                        {publishData.primary_channel && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: '#64748B', marginBottom: 2 }}>Primary Channel</div>
                            <div style={{ fontSize: 12, color: '#E2E8F0' }}>{publishData.primary_channel}</div>
                          </div>
                        )}
                        {publishData.distribution_channels?.length > 0 && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: '#64748B', marginBottom: 2 }}>Distribution</div>
                            <div style={{ fontSize: 12, color: '#CBD5E1' }}>{publishData.distribution_channels.join(' · ')}</div>
                          </div>
                        )}
                        {publishData.social_caption_linkedin && (
                          <div style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, color: '#64748B', marginBottom: 2 }}>LinkedIn Caption</div>
                            <div style={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.5 }}>{publishData.social_caption_linkedin}</div>
                          </div>
                        )}
                        {publishData.email_subject && (
                          <div>
                            <div style={{ fontSize: 10, color: '#64748B', marginBottom: 2 }}>Email Subject</div>
                            <div style={{ fontSize: 12, color: '#E2E8F0', fontStyle: 'italic' }}>{publishData.email_subject}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Valet input */}
                  <div style={{
                    flexShrink: 0, padding: '8px 10px',
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', gap: 8, alignItems: 'center',
                  }}>
                    <input
                      value={valetInput}
                      onChange={e => setValetInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendValetMessage(); }
                      }}
                      disabled={valetLoading}
                      placeholder="Ask The Herald about publishing…"
                      style={{
                        flex: 1, padding: '8px 12px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8, color: '#E2E8F0',
                        fontSize: 12, fontFamily: 'Inter, sans-serif',
                        outline: 'none',
                      }}
                    />
                    <button
                      onClick={sendValetMessage}
                      disabled={!valetInput.trim() || valetLoading}
                      style={{
                        width: 36, height: 36, flexShrink: 0,
                        background: valetInput.trim() && !valetLoading
                          ? 'linear-gradient(135deg, #F97316, #EA580C)'
                          : 'rgba(255,255,255,0.05)',
                        border: 'none', borderRadius: 8,
                        cursor: valetInput.trim() && !valetLoading ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, color: valetInput.trim() ? 'white' : '#475569',
                      }}
                    >↑</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* ── MOBILE TAB BAR ── */}
      {isMobile && (
        <div style={{
          flexShrink: 0, display: 'flex', borderTop: '1px solid rgba(255,255,255,0.08)',
          background: '#111827', height: 56, zIndex: 20,
        }}>
          {/* Back to Dashboard */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 2, border: 'none', cursor: 'pointer',
              background: 'transparent', borderRight: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <ArrowLeft size={18} color="#6B7280" />
            <span style={{ fontSize: 9, fontWeight: 600, color: '#6B7280' }}>Back</span>
          </button>
          {/* Writers Room tab */}
          <button
            onClick={() => { setMobileTab('chat'); setUnreadChat(0); }}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 2, border: 'none', cursor: 'pointer',
              background: mobileTab === 'chat' ? 'rgba(249,115,22,0.12)' : 'transparent',
              borderBottom: mobileTab === 'chat' ? '2px solid #F97316' : '2px solid transparent',
              position: 'relative',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: mobileTab === 'chat' ? '#F97316' : '#6B7280' }}>
              💬 Writers Room
            </span>
            {unreadChat > 0 && (
              <span style={{
                position: 'absolute', top: 8, right: 'calc(50% - 28px)',
                background: '#F97316', color: 'white',
                fontSize: 9, fontWeight: 800, borderRadius: 999,
                padding: '1px 5px', minWidth: 16, textAlign: 'center',
              }}>
                {unreadChat > 99 ? '99+' : unreadChat}
              </span>
            )}
          </button>
          {/* Document tab */}
          <button
            onClick={() => { setMobileTab('canvas'); setUnreadChat(0); }}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 2, border: 'none', cursor: 'pointer',
              background: mobileTab === 'canvas' ? 'rgba(249,115,22,0.12)' : 'transparent',
              borderBottom: mobileTab === 'canvas' ? '2px solid #F97316' : '2px solid transparent',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 700, color: mobileTab === 'canvas' ? '#F97316' : '#6B7280' }}>
              📄 Document
              {docContent && (
                <span style={{ marginLeft: 4, fontSize: 9, color: '#22C55E', fontWeight: 800 }}>●</span>
              )}
            </span>
          </button>
          {/* New Project */}
          <button
            onClick={() => navigate('/task-creation')}
            style={{
              width: 56, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 2, border: 'none', cursor: 'pointer',
              background: 'rgba(249,115,22,0.12)', borderLeft: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Plus size={18} color="#F97316" />
            <span style={{ fontSize: 9, fontWeight: 600, color: '#F97316' }}>New</span>
          </button>
        </div>
      )}

      {/* Mini nav pill — desktop only (mobile has tab bar above) */}
      {!isMobile && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1A2236', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 999, padding: '8px 20px', display: 'flex', gap: 20, zIndex: 100,
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        }}>
          {[
            { Icon: ArrowLeft, label: 'Dashboard', path: '/dashboard', orange: false },
            { Icon: Plus,      label: 'New',       path: '/task-creation', orange: true },
            { Icon: Archive,   label: 'Vault',     path: '/vault', orange: false },
          ].map(({ Icon, label, path, orange }) => (
            <button key={label} onClick={() => navigate(path)} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: orange ? '#F97316' : '#6B7280', padding: '2px 4px',
            }}
              onMouseEnter={e => { e.currentTarget.style.color = orange ? '#F97316' : '#F1F5F9'; }}
              onMouseLeave={e => { e.currentTarget.style.color = orange ? '#F97316' : '#6B7280'; }}
            >
              <Icon size={16} />
              <span style={{ fontSize: 10, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
